import { FunctionComponent, ReactNode } from "react";
import { Card, Typography } from "antd";
import { RightOutlined, SmileOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../hooks/AuthContext";
import { getNavGroups, NavItem } from "../../components/layout/navigationConfig";
import { UnahtorizedAccess } from "../public/UnahtorizedAccess";

interface Shortcut {
    key: string;
    label: string;
    icon?: ReactNode;
    path: string;
    state?: unknown;
}

// Aplati les items de navigation en destinations réelles (feuilles avec un `path`).
// Les enfants héritent du libellé et de l'icône de leur parent pour rester lisibles.
const collectShortcuts = (items: NavItem[], parent?: NavItem): Shortcut[] =>
    items.flatMap((item) => {
        if (item.children) {
            return collectShortcuts(item.children, item);
        }
        if (!item.path) {
            return [];
        }
        return [{
            key: item.key,
            label: parent ? `${parent.label} — ${item.label}` : item.label,
            icon: item.icon ?? parent?.icon,
            path: item.path,
            state: item.state,
        }];
    });

const getSousTitre = (roles: string[]): string => {
    if (roles.includes("ROLE_ADMIN")) return "Espace administrateur — vos accès rapides";
    if (roles.includes("ROLE_ENSEIGNANT")) return "Espace enseignant — vos accès rapides";
    if (roles.includes("ROLE_TRESORIER")) return "Espace trésorier — vos accès rapides";
    return "Vos accès rapides";
};

export const HomeAdmin: FunctionComponent = () => {
    const { username, prenom, roles } = useAuth();
    const navigate = useNavigate();

    if (!username) {
        return <UnahtorizedAccess />;
    }

    const displayName = prenom ?? username;
    const groups = getNavGroups(roles ?? []);

    return (
        <div className="centered-content">
            <div className="container-full-width">
                <div className="home-admin-hero">
                    <div className="home-admin-hero-badge">
                        <SmileOutlined style={{ fontSize: 24, color: "#ffffff" }} />
                    </div>
                    <div>
                        <Typography.Title level={3} style={{ margin: 0, lineHeight: 1.2 }}>
                            Bienvenue, {displayName}
                        </Typography.Title>
                        <Typography.Text type="secondary">{getSousTitre(roles ?? [])}</Typography.Text>
                    </div>
                </div>

                {groups.map((group, groupIndex) => {
                    const shortcuts = collectShortcuts(group.items);
                    if (shortcuts.length === 0) {
                        return null;
                    }
                    return (
                        <div key={group.label ?? `group-${groupIndex}`} className="home-admin-section">
                            {group.label && <div className="home-admin-group-title">{group.label}</div>}
                            <div className="home-admin-grid">
                                {shortcuts.map((shortcut) => (
                                    <Card
                                        key={shortcut.key}
                                        hoverable
                                        size="small"
                                        className="home-admin-card"
                                        onClick={() => navigate(shortcut.path, shortcut.state ? { state: shortcut.state } : undefined)}
                                    >
                                        <div className="home-admin-card-inner">
                                            <span className="home-admin-card-icon">{shortcut.icon}</span>
                                            <span className="home-admin-card-label">{shortcut.label}</span>
                                            <RightOutlined className="home-admin-card-arrow" />
                                        </div>
                                    </Card>
                                ))}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};
